import { useEffect, useState, type FC } from 'react';
import { Button, InputText, Select, Space, Switchbox } from '@g2u/ui-kit';
import { observer } from 'mobx-react-lite';
import { useLocation, useNavigate } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import { Form, Tabs, TablePaginationConfig } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

import { CallbackSource, TaskTypes, type JsonDataItem } from 'types';
import { CallbacksTable } from './components';
import { Toast, parseErrors } from 'helpers';
import { useTaskCount } from 'hooks/useTaskCount';
import { useTaskLock } from 'hooks/useTaskLock';
import { getTasks, putCustomerLink, putCustomerUnlink, putTaskDueDate, putToggleTaskPin } from 'queries';
import { Content } from 'components';
import { RootStore } from 'store';
import styles from './callbacks.module.scss';

export const CallbacksPage: FC = observer(() => {
  const { user } = RootStore;
  const [form] = Form.useForm();
  const location = useLocation();
  const navigate = useNavigate();
  const { removeSoftlock, addSoftlock } = useTaskLock();
  const [callbackStatus, setCallbackStatus] = useState<string>('1');
  const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([]);
  const [tablePagination, setTablePagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 5,
    showSizeChanger: true,
    pageSizeOptions: ['5', '10', '20', '50', '100'],
    className: styles.callbacks__pagination,
    showTotal: (total) => `Total ${total} item${total !== 1 ? 's' : ''}`,
  });
  const [searchParams, setSearchParams] = useState<JsonDataItem>({
    userId: user?.ID,
    filterBy: form.getFieldValue('searchBy'),
    searchValue: form.getFieldValue('searchedValue'),
    status: '1',
    taskType: TaskTypes.Callbacks,
    page: tablePagination.current || 1,
    pageSize: tablePagination.pageSize || 5,
  });

  // check tasks
  const {
    mutate: checkTasks,
    isLoading: isTasksLoading,
    data: callbacks,
  } = useMutation(getTasks, {
    onSuccess: (data: JsonDataItem) => {
      setTablePagination({
        ...tablePagination,
        current: data.meta?.current_page || 1,
        total: data.meta?.total || 0,
        pageSize: data.meta?.per_page || 5,
      });
    },
    onError: (data: JsonDataItem) => {
      Toast.show({ description: parseErrors(data).join(', '), type: Toast.Type.Error });
    },
  });

  const { refresh: refreshTaskCounter } = useTaskCount();

  // Link
  const { mutate: linkCustomer, isLoading: linkingCustomer } = useMutation(putCustomerLink, {
    onSuccess: (data, payload) => {
      Toast.show({
        description: (
          <>
            Customer file {payload.customerId} has been linked.
            <br />
            {data.data?.message}
          </>
        ),
        type: Toast.Type.Success,
      });
      checkTasks({ ...searchParams, page: location.state?.page || tablePagination.current });
    },
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  // Unlink
  const { mutate: unlinkCustomer, isLoading: unlinkingCustomer } = useMutation(putCustomerUnlink, {
    onSuccess: (_data, payload) => {
      Toast.show({
        description: `Callback ${payload.taskId} has been unlinked from customer`,
        type: Toast.Type.Success,
      });
      checkTasks(searchParams);
    },
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  // Update due date
  const { mutate: updateCallbackDate, isLoading: isCallbackDateUpdating } = useMutation(putTaskDueDate, {
    onSuccess: () => {
      Toast.show({ description: 'Due date & time has been updated.', type: Toast.Type.Success });
      checkTasks(searchParams);
    },
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  // Toggle pin
  const { mutate: toggleTaskPin } = useMutation(putToggleTaskPin, {
    onSuccess: (data: JsonDataItem) => {
      Toast.show({ description: `Task has been ${data.locked ? 'pinned' : 'unpinned'}`, type: Toast.Type.Success });
      checkTasks(searchParams);
    },
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  const handleTableFilter = (taskSource: CallbackSource | null) => {
    setSearchParams({
      ...searchParams,
      taskSource,
    });
  };

  const handleTabChange = (tab: string) => {
    setCallbackStatus(tab);
    setTablePagination({ ...tablePagination, current: 1 });
    setSearchParams({
      ...searchParams,
      status: tab,
      page: 1,
    });
  };

  const handleFormChange = (changedField: JsonDataItem) => {
    if (Object.keys(changedField).includes('showMyTask')) {
      setTablePagination({ ...tablePagination, current: 1 });
      setSearchParams({
        ...searchParams,
        page: 1,
        userId: changedField.showMyTask ? user?.ID : undefined,
      });
    }
  };

  const handleFormSubmit = (fields: JsonDataItem) => {
    setSearchParams({
      ...searchParams,
      searchValue: fields.searchValue,
      filterBy: fields.searchBy,
    });
  };

  const handleReopenCallback = () => {
    setCallbackStatus('1');
    setSearchParams({
      ...searchParams,
      status: '1',
    });
    refreshTaskCounter();
  };

  const handleUpdateCallback = () => {
    // clear the location state if closed after customer creation
    location.state?.linkCustomerId && navigate(location.pathname, { replace: true, state: null });
    checkTasks(searchParams);
  };

  useEffect(() => {
    checkTasks(searchParams);
  }, [searchParams]);

  useEffect(() => {
    // link a customer if created
    const taskId = location.state?.expandedRowId;
    const customerId = location.state?.linkCustomerId;
    if (taskId && customerId) {
      setTablePagination({ ...tablePagination, current: location.state.page });
      linkCustomer({ taskId, customerId, isNewCustomer: true });
      setExpandedRowKeys([taskId]);
    }
  }, [location.state?.linkCustomerId]);

  return (
    <Content dataTestId="tasks-page" className={styles.callbacks} hasContainer>
      <title>Callbacks</title>

      <div className={styles.callbacks}>
        <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Middle}>
          <Form
            form={form}
            layout="vertical"
            className={styles.callbacks__form}
            onValuesChange={handleFormChange}
            onFinish={handleFormSubmit}
          >
            <Space.$ fullWidth justify={Space.Justify.SpaceBetween} align={Space.Align.Start}>
              <Space.$ compact fullWidth>
                <Select.$
                  name="searchBy"
                  rules={[{}]}
                  dataTestId="search-by-select"
                  className={styles.callbacks__formSelect}
                  initialValue={'email'}
                  suffixIcon={undefined}
                  disabled // until new options are provided
                  options={[
                    {
                      label: <>Customer Email</>,
                      value: 'email',
                    },
                  ]}
                />
                <InputText.$
                  fullWidth
                  name="searchValue"
                  initialValue={''}
                  placeholder={'Search callbacks'}
                  dataTestId="search-value"
                  noMargin
                  className={styles.callbacks__formSearch}
                />
                <Button.$
                  type={Button.Type.Primary}
                  dataTestId="search-submit-btn"
                  onClick={() => null}
                  icon={<SearchOutlined />}
                  htmlType="submit"
                />
              </Space.$>

              <Switchbox.$ horizontal reverse initialValue={true} name={'showMyTask'} label="Show my tasks only" />
            </Space.$>
          </Form>
        </Space.$>

        <Tabs
          activeKey={callbackStatus}
          onChange={handleTabChange}
          items={[
            { key: '1', label: 'Open Callbacks' },
            { key: '0', label: 'Closed Callbacks' },
          ]}
        />
        <CallbacksTable
          callbacks={callbacks}
          isLoading={isTasksLoading || linkingCustomer || unlinkingCustomer || isCallbackDateUpdating}
          onUpdate={handleUpdateCallback}
          onFilter={handleTableFilter}
          onReopen={handleReopenCallback}
          onLink={linkCustomer}
          onUnlink={unlinkCustomer}
          onLock={addSoftlock}
          onUnlock={removeSoftlock}
          onPin={toggleTaskPin}
          onUpdateDueDate={updateCallbackDate}
          callbackType={callbackStatus}
          expandedRowIds={expandedRowKeys}
          linkNewCustomerId={location.state?.linkCustomerId}
          pagination={tablePagination}
          onPaginationChange={(paginationParams) => setSearchParams((params) => ({ ...params, ...paginationParams }))}
        />
      </div>
    </Content>
  );
});
